import { DateFilterOptions, NumberFilterOptions } from './types';

export const getFilterEntryId = (filterItemId: string, value = ''): string => {
  return `${filterItemId}#${value}`;
};

export const getNumberConstraintUid = (options: NumberFilterOptions): string => {
  const value = `${options.gt}_${options.gte}_${options.lt}_${options.lte}_${options.eq}_${options.ne}`;
  return value;
};

export const getDateConstraintUid = (options: DateFilterOptions): string => {
  const value = `${options.gt}_${options.gte}_${options.lt}_${options.lte}_${options.eq}_${options.ne}`;
  return value;
};

export const uniqBy = (arr: any[], predicate: string): any[] => {
  const cb = typeof predicate === 'function' ? predicate : (o: any): any => o[predicate];

  return [
    ...arr
      .reduce((map, item) => {
        const key = item === null || item === undefined ? item : cb(item);

        map.has(key) || map.set(key, item);

        return map;
      }, new Map())
      .values(),
  ];
};

export const groupBy = (arr: any[], criteria: any): any => {
  return arr.reduce(function (obj, item) {
    // Check if the criteria is a function to run on the item or a property of it
    const key = typeof criteria === 'function' ? criteria(item) : item[criteria];

    // If the key doesn't exist yet, create it
    if (!obj.hasOwnProperty(key)) {
      obj[key] = [];
    }

    // Push the value to the object
    obj[key].push(item);

    // Return the object to the next item in the loop
    return obj;
  }, {});
};

export function generateKey(length = 10): string {
  let text = '';
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  for (let i = 0; i < length; i++) text += possible.charAt(Math.floor(Math.random() * possible.length));

  return text;
}

export const removeTrailingSlash = (str: string): string => {
  return str.replace(/\/+$/, '');
};

export const escapeRegExp = (text: string): string => {
  if (typeof text !== 'string') return '';

  return text.replace(/[[\]{}()*+?.,\\^$|#]/g, '\\$&');
  /**
   * original regex
   * remove \s -
   */
  // return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};

export const unescapeRegExp = (escapedText: string): string => {
  if (typeof escapedText !== 'string') return '';

  return escapedText.replace(/\\([[\]{}()*+?.,\\^$|#])/g, '$1');
};

export const hasMinimumVersion = (version: string, minVersion: string): boolean => {
  try {
    return compareVersions(version, minVersion) >= 0;
  } catch (err) {
    return false;
  }
};

/**
 * Compares two semantic version strings.
 * @param v1 - First version string (e.g., "1.10.2").
 * @param v2 - Second version string (e.g., "1.9.0").
 * @returns A number: -1 if v1 < v2, 1 if v1 > v2, 0 if equal.
 * @throws Error if the input is not a valid semantic version.
 */
export const compareVersions = (v1: string, v2: string): number => {
  if (!isValidVersion(v1) || !isValidVersion(v2)) {
    throw new Error(`Invalid version format: v1="${v1}", v2="${v2}". Expected format: "x.y.z"`);
  }

  const v1Parts = v1.split('.').map(Number);
  const v2Parts = v2.split('.').map(Number);

  for (let i = 0; i < 3; i++) {
    const num1 = v1Parts[i] || 0; // Default to 0 if undefined (e.g., "1.2" treated as "1.2.0")
    const num2 = v2Parts[i] || 0;

    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }

  return 0;
};

/**
 * Validates if a string follows semantic versioning (x.y.z).
 * @param version - Version string to validate.
 * @returns True if valid, otherwise false.
 */
export const isValidVersion = (version: string): boolean => {
  return /^\d+(\.\d+){0,2}$/.test(version);
};
